import { BlockPost, Page, PageBlock, Post, Redirect, Schema } from '@/types/directus-schema';
import { useDirectus } from './directus';
import { readItems, aggregate, readItem, readSingleton, withToken, QueryFilter } from '@directus/sdk';
import { RedirectError } from '../redirects';
import { logger } from '../logger';
import { serializeError } from '../serialize-error';

/**
 * Helper to get the cleaned public token from environment variables
 */
const getPublicToken = () => process.env.DIRECTUS_PUBLIC_TOKEN?.split('#')[0].trim();

const postBlockFields = [
	{
		blocks: [
			'id',
			'collection',
			'item',
			'sort',
			'hide_block',
			{
				item: {
					block_richtext: ['*', 'id', 'content', 'alignment', 'container_width'],
					block_gallery: ['*', 'id', 'tagline', 'headline', { items: ['id', 'directus_file', 'sort'] }],
					block_photo_collage: [
						'*',
						'id',
						'title',
						'background_color',
						{
							images: [
								'id',
								'caption',
								'sort',
								{ directus_files_id: ['id', 'filename_disk', 'title', 'width', 'height'] },
							],
						},
					],
					block_pricing: [
						'*',
						'id',
						'tagline',
						'headline',
						{
							pricing_cards: [
								'id',
								'title',
								'description',
								'price',
								'badge',
								'features',
								'is_highlighted',
								{
									button: [
										'id',
										'label',
										'variant',
										'url',
										'type',
										'icon',
										'icon_position',
										{ page: ['permalink'] },
										{ post: ['slug'] },
									],
								},
							],
						},
					],
					block_hero: [
						'*',
						'id',
						'tagline',
						'headline',
						'cta_text',
						'cta_link',
						{
							hero_images: [
								'id',
								{
									directus_files_id: ['id', 'filename_disk', 'title'],
								},
							],
						},
					],
					block_posts: ['*', 'id', 'tagline', 'headline', 'collection', 'limit'],
					block_form: [
						'*',
						'id',
						'tagline',
						'headline',
						{
							form: [
								'id',
								'title',
								'submit_label',
								'success_message',
								'on_success',
								'success_redirect_url',
								'is_active',
								{
									fields: [
										'id',
										'name',
										'type',
										'label',
										'placeholder',
										'help',
										'validation',
										'width',
										'choices',
										'required',
										'sort',
									],
								},
							],
						},
					],
					block_trust_indicators: ['*', 'id', 'indicators'],
					block_testimonials: [
						'*',
						'id',
						'title',
						'background',
						'region',
						{ testimonials: ['name', 'location', 'rating', 'text', 'date', 'region'] },
					],
					block_partners: ['*', 'id', 'title', { partners: ['id', 'name', { logo: ['id', 'filename_disk'] }, 'url'] }],
					block_servicearea: ['*', 'id', 'title', 'description', { areas: ['name', 'slug'] }],
					block_services: [
						'*',
						'id',
						'title',
						'description',
						'service_items',
						{ image: ['id', 'filename_disk', 'title', 'description'] },
					],
					block_faq: ['*', 'id', 'title', 'headline', 'description', 'faqs'],
					block_quote_form: ['*', 'id', 'show_logo', 'show_toggle', 'intro_content', 'show_intro', 'show_form'],
					block_business_quote_form: ['*', 'id', 'show_logo', 'show_toggle', 'intro_content', 'show_intro', 'show_form'],
					block_contact_form: ['*', 'id', 'intro_content', 'show_intro', 'show_form'],
					block_content_image: [
						'*',
						'id',
						'headline',
						'content',
						{ image: ['id', 'filename_disk', 'title', 'description'] },
						'image_position',
						'background_color',
						{
							button_group: [
								'id',
								{
									buttons: [
										{
											block_button_id: [
												'*',
												'id',
												'label',
												'variant',
												'url',
												'type',
												'icon',
												'icon_position',
												{ page: ['permalink'] },
												{ post: ['slug'] },
											],
										},
									],
								},
							],
						},
					],
					block_multicolumn: [
						'*',
						'id',
						'background_color',
						'title',
						'image_size',
						'col_1_enabled',
						{ col_1_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_1_content',
						'col_2_enabled',
						{ col_2_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_2_content',
						'col_3_enabled',
						{ col_3_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_3_content',
						'col_4_enabled',
						{ col_4_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_4_content',
						'col_5_enabled',
						{ col_5_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_5_content',
						{
							button_group: [
								'id',
								{
									block_button_group_id: [
										'*',
										'id',
										{
											buttons: [
												{
													block_button_id: [
														'*',
														'id',
														'label',
														'variant',
														'url',
														'type',
														'icon',
														'icon_position',
														{ page: ['permalink'] },
														{ post: ['slug'] },
													],
												},
											],
										},
									],
								},
								{
									buttons: [
										{
											block_button_id: [
												'*',
												'id',
												'label',
												'variant',
												'url',
												'type',
												'icon',
												'icon_position',
												{ page: ['permalink'] },
												{ post: ['slug'] },
											],
										},
									],
								},
							],
						},
						{
							buttons: [
								'id',
								{
									block_button_id: [
										'*',
										'id',
										'label',
										'variant',
										'url',
										'type',
										'icon',
										'icon_position',
										{ page: ['permalink'] },
										{ post: ['slug'] },
									],
								},
							],
						},
					],
					block_simple_hero: [
						'*',
						'id',
						'headline',
						'tagline',
						'background_type',
						{ background_image: ['id', 'filename_disk', 'title', 'description'] },
						'alignment',
						{
							button_group: [
								'id',
								{
									buttons: [
										{
											block_button_id: [
												'*',
												'id',
												'label',
												'variant',
												'url',
												'type',
												'icon',
												'icon_position',
												{ page: ['permalink'] },
												{ post: ['slug'] },
											],
										},
									],
								},
							],
						},
					],
					block_project_showcase: [
						'*',
						'id',
						'headline',
						'background',
						{
							items: [
								'id',
								'project_items_id',
								{
									project_items_id: ['id', 'title', 'description', 'image'],
								},
							],
						},
					],

				},
			},
		],
	},
];

/**
 * Page fields configuration for Directus queries
 */
const pageFields = [
	'title',
	'seo',
	'id',
	'display_topbar',
	'display_mainnav',
	{
		blocks: [
			'id',
			'collection',
			'item',
			'sort',
			'hide_block',
			{
				item: {
					block_richtext: ['*', 'id', 'content', 'alignment', 'container_width'],
					block_gallery: ['*', 'id', 'tagline', 'headline', { items: ['id', 'directus_file', 'sort'] }],
					block_photo_collage: [
						'*',
						'id',
						'title',
						'background_color',
						{
							images: [
								'id',
								'caption',
								'sort',
								{ directus_files_id: ['id', 'filename_disk', 'title', 'width', 'height'] },
							],
						},
					],
					block_pricing: [
						'*',
						'id',
						'tagline',
						'headline',
						{
							pricing_cards: [
								'id',
								'title',
								'description',
								'price',
								'badge',
								'features',
								'is_highlighted',
								{
									button: [
										'id',
										'label',
										'variant',
										'url',
										'type',
										'icon',
										'icon_position',
										{ page: ['permalink'] },
										{ post: ['slug'] },
									],
								},
							],
						},
					],
					block_hero: [
						'*',
						'id',
						'tagline',
						'headline',
						'cta_text',
						'cta_link',
						{
							hero_images: [
								'id',
								{
									directus_files_id: ['id', 'filename_disk', 'title'],
								},
							],
						},
					],
					block_posts: ['*', 'id', 'tagline', 'headline', 'collection', 'limit'],
					block_form: [
						'*',
						'id',
						'tagline',
						'headline',
						{
							form: [
								'id',
								'title',
								'submit_label',
								'success_message',
								'on_success',
								'success_redirect_url',
								'is_active',
								{
									fields: [
										'id',
										'name',
										'type',
										'label',
										'placeholder',
										'help',
										'validation',
										'width',
										'choices',
										'required',
										'sort',
									],
								},
							],
						},
					],
					block_trust_indicators: ['*', 'id', 'indicators'],
					block_testimonials: [
						'*',
						'id',
						'title',
						'background',
						'region',
						{ testimonials: ['name', 'location', 'rating', 'text', 'date', 'region'] },
					],
					block_partners: ['*', 'id', 'title', { partners: ['id', 'name', { logo: ['id', 'filename_disk'] }, 'url'] }],
					block_servicearea: ['*', 'id', 'title', 'description', { areas: ['name', 'slug'] }],
					block_services: [
						'*',
						'id',
						'title',
						'description',
						'service_items',
						{ image: ['id', 'filename_disk', 'title', 'description'] },
					],
					block_faq: ['*', 'id', 'title', 'headline', 'description', 'faqs'],
					block_quote_form: ['*', 'id', 'show_logo', 'show_toggle', 'intro_content', 'show_intro', 'show_form'],
					block_business_quote_form: ['*', 'id', 'show_logo', 'show_toggle', 'intro_content', 'show_intro', 'show_form'],
					block_contact_form: ['*', 'id', 'intro_content', 'show_intro', 'show_form'],
					block_content_image: [
						'*',
						'id',
						'headline',
						'content',
						{ image: ['id', 'filename_disk', 'title', 'description'] },
						'image_position',
						'background_color',
						{
							button_group: [
								'id',
								{
									buttons: [
										{
											block_button_id: [
												'*',
												'id',
												'label',
												'variant',
												'url',
												'type',
												'icon',
												'icon_position',
												{ page: ['permalink'] },
												{ post: ['slug'] },
											],
										},
									],
								},
							],
						},
					],
					block_multicolumn: [
						'*',
						'id',
						'background_color',
						'title',
						'image_size',
						'col_1_enabled',
						{ col_1_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_1_content',
						'col_2_enabled',
						{ col_2_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_2_content',
						'col_3_enabled',
						{ col_3_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_3_content',
						'col_4_enabled',
						{ col_4_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_4_content',
						'col_5_enabled',
						{ col_5_image: ['id', 'filename_disk', 'title', 'description'] },
						'col_5_content',
						{
							button_group: [
								'id',
								// If button_group is an M2M junction:
								{
									block_button_group_id: [
										'*',
										'id',
										{
											buttons: [
												{
													block_button_id: [
														'*',
														'id',
														'label',
														'variant',
														'url',
														'type',
														'icon',
														'icon_position',
														{ page: ['permalink'] },
														{ post: ['slug'] },
													],
												},
											],
										},
									],
								},
								// If button_group is an M2O relation:
								{
									buttons: [
										{
											block_button_id: [
												'*',
												'id',
												'label',
												'variant',
												'url',
												'type',
												'icon',
												'icon_position',
												{ page: ['permalink'] },
												{ post: ['slug'] },
											],
										},
									],
								},
							],
						},
						// Fallback if configured as direct M2M
						{
							buttons: [
								'id',
								{
									block_button_id: [
										'*',
										'id',
										'label',
										'variant',
										'url',
										'type',
										'icon',
										'icon_position',
										{ page: ['permalink'] },
										{ post: ['slug'] },
									],
								},
							],
						},
					],
					block_simple_hero: [
						'*',
						'id',
						'headline',
						'tagline',
						'background_type',
						{ background_image: ['id', 'filename_disk', 'title', 'description'] },
						'alignment',
						{
							button_group: [
								'id',
								{
									buttons: [
										{
											block_button_id: [
												'*',
												'id',
												'label',
												'variant',
												'url',
												'type',
												'icon',
												'icon_position',
												{ page: ['permalink'] },
												{ post: ['slug'] },
											],
										},
									],
								},
							],
						},
					],
					block_project_showcase: [
						'*',
						'id',
						'headline',
						'background',
						{
							items: [
								'id',
								'project_items_id', // M2M relation ID
								{
									project_items_id: ['id', 'title', 'description', 'image'], // Expanded M2M item
								},
							],
						},
					],

				},
			},
		],
	},
];

/**
 * Fetches page data by permalink, including all nested blocks and dynamically fetching blog posts if required.
 */
export const fetchPageData = async (permalink: string, postPage = 1, token?: string, preview?: boolean) => {
	const { directus } = useDirectus();
	const activeToken = token || getPublicToken();

	try {
		const query = {
			filter:
				preview && token
					? { permalink: { _eq: permalink } }
					: { permalink: { _eq: permalink }, status: { _eq: 'published' } },
			limit: 1,
			fields: pageFields as any,
			deep: {
				blocks: { _sort: ['sort'], _filter: { hide_block: { _neq: true } } },
			} as any,
		};

		const pageData = (await directus.request(
			activeToken ? withToken(activeToken, readItems('pages', query)) : readItems('pages', query),
		)) as unknown as Page[];

		if (!pageData || !pageData.length) {
			return null;
		}

		const page = pageData[0];

		if (Array.isArray(page.blocks)) {
			for (const block of page.blocks as PageBlock[]) {

				if (
					block.collection === 'block_posts' &&
					block.item &&
					typeof block.item !== 'string' &&
					'collection' in block.item &&
					block.item.collection === 'posts'
				) {
					const blockPost = block.item as BlockPost;
					const limit = blockPost.limit ?? 6;

					const posts: Post[] = await directus.request(
						readItems('posts', {
							fields: ['id', 'title', 'description', 'slug', 'image', 'published_at', 'status'],
							filter: { status: { _eq: 'published' } },
							sort: ['-published_at'],
							limit,
							page: postPage,
						}),
					);

					(block.item as BlockPost & { posts: Post[] }).posts = posts;
				}

				// Resolve block_featuredlinks: fetch all data in separate steps
				if (
					block.collection === 'block_featuredlinks' &&
					block.item &&
					(typeof block.item === 'string' || typeof block.item === 'number')
				) {
					const blockId = block.item;
					try {
						// 1. Get junction entries to find item IDs
						const junctionEntries = (await directus.request(
							readItems('block_featuredlinks_block_featuredlinks_item' as any, {
								filter: { block_featuredlinks_id: { _eq: blockId } },
								fields: ['id', 'block_featuredlinks_item_id'] as any,
							}),
						)) as any[];

						const itemIds = junctionEntries
							.map((j) => j.block_featuredlinks_item_id)
							.filter(Boolean);

						let resolvedItems: any[] = [];
						if (itemIds.length > 0) {
							// 2. Fetch full item data with M2A relations
							resolvedItems = (await directus.request(
								readItems('block_featuredlinks_item' as any, {
									filter: { id: { _in: itemIds } },
									fields: [
										'id',
										'title',
										'description',
										{ image: ['id', 'filename_disk', 'title', 'description'] },
										{
											item: [
												'id',
												'collection',
												{
													item: {
														pages: ['id', 'title', 'permalink'],
														posts: ['id', 'title', 'slug'],
													},
												},
											],
										},
									] as any,
								}),
							)) as any[];
						}

						// 3. Attach resolved data as block.item object
						block.item = {
							id: blockId,
							items: junctionEntries.map((j) => ({
								id: j.id,
								block_featuredlinks_item_id: resolvedItems.find(
									(fi) => fi.id === j.block_featuredlinks_item_id,
								),
							})),
						} as any;
					} catch (err) {
						// Silently continue if resolution fails
					}
				}
			}
		}

		return page;
	} catch (error: any) {
		logger.error('Error fetching page data', {
			message: error.message,
			status: error.response?.status,
			errors: JSON.stringify(error.errors || error.response?.data?.errors || error.response?.errors, null, 2),
			tokenProvided: !!activeToken,
		});

		return null;
	}
};

/**
 * Fetches page data by id and version
 */
export const fetchPageDataById = async (id: string, version?: string, token?: string): Promise<Page> => {
	if (!id || id.trim() === '') throw new Error('Invalid id');
	if (!version || version.trim() === '') throw new Error('Invalid version');

	const { directus } = useDirectus();
	const activeToken = token || undefined;

	try {
		const query = {
			version,
			fields: pageFields as any,
			deep: {
				blocks: { _sort: ['sort'], _filter: { hide_block: { _neq: true } } },
			} as any,
		};

		return (await directus.request(
			activeToken ? withToken(activeToken, readItem('pages', id, query)) : readItem('pages', id, query),
		)) as unknown as Page;
	} catch (error) {
		logger.error('Error fetching versioned page', serializeError(error));
		throw new Error('Failed to fetch versioned page');
	}
};

/**
 * Helper function to get page ID by permalink
 */
export const getPageIdByPermalink = async (permalink: string, token?: string) => {
	if (!permalink || permalink.trim() === '') throw new Error('Invalid permalink');

	const { directus } = useDirectus();
	const activeToken = token || getPublicToken();

	try {
		const query = {
			filter: { permalink: { _eq: permalink } },
			limit: 1,
			fields: ['id'] as any,
		};

		const pageData = (await directus.request(
			activeToken ? withToken(activeToken, readItems('pages', query)) : readItems('pages', query),
		)) as unknown as Pick<Page, 'id'>[];

		return pageData.length > 0 ? pageData[0].id : null;
	} catch (error) {
		logger.error('Error getting page ID', serializeError(error));

		return null;
	}
};

/**
 * Helper function to get post ID by slug
 */
export const getPostIdBySlug = async (slug: string, token?: string) => {
	if (!slug || slug.trim() === '') throw new Error('Invalid slug');

	const { directus } = useDirectus();
	const activeToken = token || getPublicToken();

	try {
		const query = {
			filter: { slug: { _eq: slug } },
			limit: 1,
			fields: ['id'] as any,
		};

		const postData = (await directus.request(
			activeToken ? withToken(activeToken, readItems('posts', query)) : readItems('posts', query),
		)) as unknown as Pick<Post, 'id'>[];

		return postData.length > 0 ? postData[0].id : null;
	} catch (error) {
		logger.error('Error getting post ID', serializeError(error));

		return null;
	}
};

/**
 * Fetches a single blog post by ID and version
 */
export const fetchPostByIdAndVersion = async (
	id: string,
	version: string,
	slug: string,
	token?: string,
): Promise<{ post: Post; relatedPosts: Post[] }> => {
	const { directus } = useDirectus();

	try {
		const [postData, relatedPosts] = await Promise.all([
			directus.request(
				token
					? withToken(
							token,
							readItem('posts', id, {
								version,
								fields: [
									'id',
									'title',
									'status',
									'published_at',
									'image',
									'description',
									'slug',
									'seo',
									{ author: ['id', 'first_name', 'last_name', 'avatar'] },
									...postBlockFields,
								] as any,
								deep: { blocks: { _sort: ['sort', 'id'] } } as any,
							}),
						)
					: readItem('posts', id, {
							version,
							fields: [
								'id',
								'title',
								'status',
								'published_at',
								'image',
								'description',
								'slug',
								'seo',
								{ author: ['id', 'first_name', 'last_name', 'avatar'] },
								...postBlockFields,
							] as any,
							deep: { blocks: { _sort: ['sort', 'id'] } } as any,
						}),
			),
			directus.request(
				readItems('posts', {
					filter: { slug: { _neq: slug }, status: { _eq: 'published' } },
					limit: 2,
					fields: ['id', 'title', 'slug', 'image'] as any,
				}),
			),
		]);

		return { post: postData as unknown as Post, relatedPosts: relatedPosts as unknown as Post[] };
	} catch (error) {
		logger.error('Error fetching versioned post', serializeError(error));
		throw new Error('Failed to fetch versioned post');
	}
};

/**
 * Fetches global site data, header navigation, and footer navigation.
 */
export const fetchSiteData = async () => {
	const { directus } = useDirectus();
	const activeToken = getPublicToken();

	try {
		const globalsQuery = {
			fields: [
				'id',
				'title',
				'description',
				'logo',
				'logo_dark_mode',
				'social_links',
				'accent_color',
				'favicon',
				'announcement_enabled',
				'announcement_text',
				'popover_enabled',
				'popover_text',
			] as any,
		};
		const navQuery = (id: string) => ({
			fields: [
				'id',
				'title',
				{
					items: [
						'id',
						'title',
						'type',
						'url',
						{ page: ['permalink'] },
						{
							children: ['id', 'title', 'type', 'url', { page: ['permalink'] }],
						},
					],
				},
			] as any,
			deep: {
				items: {
					_sort: ['sort'],
					children: {
						_sort: ['sort'],
					},
				},
			} as any,
		});

		const requestGlobals = directus.request(
			activeToken
				? withToken(activeToken, readSingleton('globals', globalsQuery))
				: readSingleton('globals', globalsQuery),
		);

		const requestHeader = directus.request(
			activeToken
				? withToken(activeToken, readItem('navigation', 'main', navQuery('main')))
				: readItem('navigation', 'main', navQuery('main')),
		);

		const requestFooter = directus.request(
			activeToken
				? withToken(activeToken, readItem('navigation', 'footer', navQuery('footer')))
				: readItem('navigation', 'footer', navQuery('footer')),
		);

		const [globals, headerNavigation, footerNavigation] = await Promise.all([
			requestGlobals,
			requestHeader,
			requestFooter,
		]);

		return { globals, headerNavigation, footerNavigation };
	} catch (error) {
		logger.error('Error fetching site data', serializeError(error));
		throw new Error('Failed to fetch site data');
	}
};

/**
 * Fetches a single blog post by slug and related blog posts excluding the given ID. Handles live preview mode.
 */
export const fetchPostBySlug = async (
	slug: string,
	options?: { draft?: boolean; token?: string },
): Promise<{ post: Post | null; relatedPosts: Post[] }> => {
	const { directus } = useDirectus();
	const { draft, token } = options || {};
	const publicToken = getPublicToken();
	const activeToken = token || publicToken;

	try {
		const filter: QueryFilter<Schema, Post> =
			activeToken || draft ? { slug: { _eq: slug } } : { slug: { _eq: slug }, status: { _eq: 'published' } };

		const [posts, relatedPosts] = await Promise.all([
			directus.request<Post[]>(
				activeToken
					? withToken(
							activeToken,
							readItems('posts', {
								filter,
								limit: 1,
								fields: [
									'id',
									'title',
									'status',
									'published_at',
									'image',
									'description',
									'slug',
									'seo',
									{ author: ['id', 'first_name', 'last_name', 'avatar'] },
									...postBlockFields,
								] as any,
								deep: { blocks: { _sort: ['sort', 'id'] } } as any,
							}),
						)
					: readItems('posts', {
							filter,
							limit: 1,
							fields: [
								'id',
								'title',
								'status',
								'published_at',
								'image',
								'description',
								'slug',
								'seo',
								{ author: ['id', 'first_name', 'last_name', 'avatar'] },
								...postBlockFields,
							] as any,
							deep: { blocks: { _sort: ['sort', 'id'] } } as any,
						}),
			),
			directus.request<Post[]>(
				activeToken
					? withToken(
							activeToken,
							readItems('posts', {
								filter: { slug: { _neq: slug }, status: { _eq: 'published' } },
								limit: 2,
								fields: ['id', 'title', 'slug', 'image'] as any,
							}),
						)
					: readItems('posts', {
							filter: { slug: { _neq: slug }, status: { _eq: 'published' } },
							limit: 2,
							fields: ['id', 'title', 'slug', 'image'] as any,
						}),
			),
		]);

		const post: Post | null = posts.length > 0 ? (posts[0] as Post) : null;

		return { post, relatedPosts };
	} catch (error: any) {
		logger.error('Error in fetchPostBySlug', serializeError(error));
		throw new Error('Failed to fetch blog post and related posts');
	}
};

/**
 * Fetches paginated blog posts.
 */
export const fetchPaginatedPosts = async (limit: number, page: number): Promise<Post[]> => {
	const { directus } = useDirectus();
	try {
		const response = (await directus.request(
			readItems('posts', {
				limit,
				page,
				sort: ['-published_at'],
				fields: ['id', 'title', 'description', 'slug', 'image'] as any,
				filter: { status: { _eq: 'published' } },
			}),
		)) as unknown as Post[];

		return response;
	} catch (error) {
		logger.error('Error fetching paginated posts', serializeError(error));
		throw new Error('Failed to fetch paginated posts');
	}
};

/**
 * Fetches the total number of published blog posts.
 */
export const fetchTotalPostCount = async (): Promise<number> => {
	const { directus } = useDirectus();

	try {
		const response = await directus.request(
			aggregate('posts', {
				aggregate: { count: '*' },
				filter: { status: { _eq: 'published' } },
			}),
		);

		return Number(response[0]?.count) || 0;
	} catch (error) {
		logger.error('Error fetching total post count', serializeError(error));

		return 0;
	}
};

export async function fetchRedirects(): Promise<Pick<Redirect, 'url_from' | 'url_to' | 'response_code'>[]> {
	const { directus } = useDirectus();
	const response = await directus.request(
		readItems('redirects', {
			filter: {
				_and: [{ url_from: { _nnull: true } }, { url_to: { _nnull: true } }],
			},
			fields: ['url_from', 'url_to', 'response_code'] as any,
		}),
	);

	return (response as unknown as Pick<Redirect, 'url_from' | 'url_to' | 'response_code'>[]) || [];
}
